import React from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useForm } from 'react-hook-form';
import { useToast } from '../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { classNames } from 'primereact/utils';
import { Button, FloatLabel, InputText, SCIcon, Controller, Path, SubmitHandler, PasswordField, } from "../../Common/Imports"
import { User_EXT } from '../../Models/Classes';
import { ValidateEmail } from '../../Common/Utility/ValidateEmail';

interface UserNewProps {
    stakeholderId: number;
    OnSave?: () => void;
}

const UserNew: React.FC<UserNewProps> = ({ stakeholderId, OnSave }) => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<User_EXT>();
    const AxiosHelper = useAxios();

    const onSubmit: SubmitHandler<User_EXT> = async (data: User_EXT) => {
        data.idStakeholder = stakeholderId;
        data.userName = data.email;
        try {
            await AxiosHelper.post(`user/CreateUser`, data);
            showToast('success', 'Success', t("Common.Toasts.New.Success"));
            if (OnSave) {
                OnSave();
            }
        } catch (error: unknown) {
            console.log(error);
            showToast('error', 'Error', t("Common.Toasts.New.Error"));
        }
    };

    const password = watch('password');

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
            <div className="row mb-3 mt-3">
                <div className="col-md-6 mb-4">
                    <Controller
                        name="name"
                        control={control}
                        rules={{ required: t("User.New.Name") + " " + t("Auth.Register.Form.Required") }}
                        render={({ field }) => (
                            <FloatLabel>
                                <InputText
                                    id="UserName"
                                    {...field}
                                    className={classNames({ 'p-invalid': errors.name })}
                                />
                                <label htmlFor="UserName">{t("User.New.Name")}</label>
                            </FloatLabel>
                        )}
                    />
                    {errors.name && (
                        <small className="p-error">
                            {errors.name.message}
                        </small>
                    )}
                </div>

                <div className="col-md-6 mb-4">
                    <Controller
                        name="surname"
                        control={control}
                        rules={{ required: t("User.New.Surname") + " " + t("Auth.Register.Form.Required") }}
                        render={({ field }) => (
                            <FloatLabel>
                                <InputText
                                    id="UserSurname"
                                    {...field}
                                    className={classNames({ 'p-invalid': errors.surname })}
                                />
                                <label htmlFor="UserSurname">{t("User.New.Surname")}</label>
                            </FloatLabel>
                        )}
                    />
                    {errors.surname && (
                        <small className="p-error">
                            {errors.surname.message}
                        </small>
                    )}
                </div>

                <div className="col-md-12 mb-4">
                    <Controller
                        name="email"
                        control={control}
                        rules={{
                            required: t("User.New.Email") + " " + t("Common.Form.Required"),
                            validate: (value) =>
                                value && ValidateEmail(value) || t("User.New.Email") + " " + t("Common.Form.Invalid")
                        }}
                        render={({ field }) => (
                            <FloatLabel>
                                <InputText
                                    id="email"
                                    {...field}
                                    className={classNames({ 'p-invalid': errors.email })}
                                />
                                <label htmlFor="UserEmail">{t("User.New.Email")}</label>
                            </FloatLabel>
                        )}
                    />
                    {errors.email && (
                        <small className="p-error">
                            {errors.email.message}
                        </small>
                    )}
                </div>
                <div className="col-md-6 mb-4">
                    <PasswordField<User_EXT> control={control} name="password" label={t("User.New.Password")} />
                </div>
                <div className="col-md-6 mb-4">
                    <PasswordField<User_EXT>
                        control={control}
                        name="repeatpassword"
                        label={t("User.New.RepeatPassword")}
                        validationRules={{
                            validate: (value) => value && value === password || t("Auth.Register.Form.User.PasswordRepeat") + " " + t("Auth.Register.Form.Mismatch"),
                            deps: ['repeatpassword'] as Path<User_EXT>[], // Correctly type deps
                        }}
                    />
                </div>

                <div className="row">
                    <div className="col">
                        <Button type="submit" label={t("Common.Buttons.Save")} icon={<SCIcon Name="Save" />} />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default UserNew;
